import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { User, Cluster, ClusterNote, Identity } from './types'
import { acceptCameraView, imageUrl, fetchMe, fetchClusters, fetchClusterDetail, updateClusterAck, addClusterNote, speak, fetchPhrases } from './api'
import { FilterButton } from './components/FilterPanel'
import { CameraView } from './components/CameraView'
import { CameraPicker } from './components/CameraPicker'
import { Drawer } from './components/Drawer'
import { LatestIncident } from './components/LatestIncident'
import { MapsPage } from './components/MapsPage'
import { isInProgress } from './activity'
import { FilterStrip } from './components/FilterStrip'
import { useSites } from './useSites'
import { noticeKey, useArming, type ArmEvent, type ArmPendingEvent, type ArmPostponedEvent } from './useArming'
import { ArmAnnouncer, ArmNotices } from './components/ArmNotices'
import { ClustersSidebar } from './components/ClustersSidebar'
import { ClusterDispatchPanel } from './components/ClusterDispatchPanel'
import { SpeakPanel } from './components/SpeakPanel'
import { SiteMapPane } from './components/SiteMapPane'
import { useCameraHealth } from './useCameraHealth'
import { useSpeaker } from './useSpeaker'
import { useTamper } from './useTamper'
import { siteIssues } from './siteIssues'
import { useSiteFilter } from './useSiteFilter'
import { allowsCamera, allowsSite, filterClusters, isNarrowed, NO_FILTER, type SiteFilter } from './siteFilter'
import { SiteDashboard } from './components/SiteDashboard'
import { StreamPanel, type StreamLayout } from './components/StreamPanel'
import { AccountMenu } from './components/AccountMenu'
import { TileSize } from './components/TileSize'
import { armsAt } from './armTime'
import { IssuesIndicator } from './components/IssuesIndicator'
import owLogo from './assets/White-300x65.webp'
import { applyTheme } from './themes'
import { useLayout } from './useLayout'
import { incidentTitle } from './incidentTitle'
import { useCanSpeak } from './useCanSpeak'

type Panel =
  | { type: 'stream'; siteId: string; camera: string }
  | null

// Alerts is a view of its own only on a phone, which has no room for the rail;
// anywhere wider it is the wall, with the rail beside it.
type View = 'sites' | 'maps' | 'alerts'
const views: { key: View; name: string; href: string }[] = [
  { key: 'sites', name: 'Sites', href: '#/' },
  { key: 'maps', name: 'Maps', href: '#/maps' },
]
const phoneTabs: { key: View; name: string; href: string; icon: string }[] = [
  { key: 'sites', name: 'Sites', href: '#/', icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z' },
  { key: 'alerts', name: 'Alerts', href: '#/alerts', icon: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0' },
  { key: 'maps', name: 'Maps', href: '#/maps', icon: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14' },
]
const viewOf = (hash: string): View => (hash === '#/maps' ? 'maps' : hash === '#/alerts' ? 'alerts' : 'sites')

export default function App() {
  const [clusters, setClusters] = useState<Cluster[]>([])
  const [clusterNewIds, setClusterNewIds] = useState<Set<number>>(new Set())
  // Said to a screen reader as each alert arrives, which otherwise learned of
  // one only by going to look (#287).
  const [announcement, setAnnouncement] = useState('')
  const [clusterStatus, setClusterStatus] = useState<'loading' | 'ok' | 'error' | 'live'>('loading')
  const [clusterError, setClusterError] = useState('')
  const [panel, setPanel] = useState<Panel>(null)
  // The live view the speak panel was opened from. Tied to that selection, not
  // just its camera, so any change of view — another camera above all — closes
  // the panel rather than leaving it speaking to somewhere the dispatcher has
  // stopped looking at.
  const [speakingFrom, setSpeakingFrom] = useState<Panel>(null)
  // Asked for each time the panel opens: an administrator can change them at
  // any time. Only the latest ask may fill them, so a slow answer from an
  // earlier opening cannot overwrite a newer one.
  const [phrases, setPhrases] = useState<string[]>([])
  const phrasesAsked = useRef(0)
  const openSpeak = useCallback((from: Panel) => {
    setSpeakingFrom(from)
    setPhrases([])
    const ask = ++phrasesAsked.current
    fetchPhrases()
      .then((list) => { if (ask === phrasesAsked.current) setPhrases(list.map((p) => p.text)) })
      .catch(() => {}) // the panel still speaks typed text without them
  }, [])
  const speaking = panel?.type === 'stream' && speakingFrom === panel ? panel : null
  // Speaking to the camera in view, from live view or an open incident.
  const speakToPanel = useCallback(() => {
    if (panel?.type === 'stream') openSpeak(panel)
  }, [panel, openSpeak])
  const [selection, setSelection] = useState<{ clusterId: number; identity: Identity } | null>(null)
  // Keyed by cluster id so switching clusters can't mix their notes — derived
  // rather than reset from inside the effect.
  const [notesState, setNotesState] = useState<{ id: number | null; notes: ClusterNote[] }>({ id: null, notes: [] })
  const selectionRef = useRef(selection)
  const selectedClusterId = selection?.clusterId ?? null
  const clusterNotes = notesState.id === selectedClusterId ? notesState.notes : []
  const siteList = useSites()
  const arming = useArming(siteList.list)
  const { onArm, onArmPending, onArmPostponed, refresh: refreshArming } = arming
  const sites = siteList.byId
  // One poll per site, shared by the wall, Maps, the camera picker and the player.
  const { health, unreachable } = useCameraHealth(sites)
  const speaker = useSpeaker(panel?.type === 'stream' ? panel : null)
  const canSpeak = useCanSpeak(panel?.type === 'stream' ? panel.siteId : null, panel?.type === 'stream' ? panel.camera : null)
  const tamper = useTamper(sites)
  const issues = useMemo(
    () => siteIssues(sites, health, unreachable, tamper),
    [sites, health, unreachable, tamper],
  )
  // A view preference, not a permission: it narrows what the server has already
  // scoped to this user, so it can only ever show less.
  const { filter, applyFilter } = useSiteFilter()
  const visibleClusters = filterClusters(clusters, filter)
  const [cardSize, setCardSize] = useState(280)
  const [eventsSidebarOpen, setEventsSidebarOpen] = useState(true)
  const layout = useLayout()
  // On a tablet the alerts are a drawer over the wall rather than a rail
  // beside it, which left an open incident 56px (#284).
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setDrawerOpen(false), [setDrawerOpen])
  // Closed by leaving a tablet's width, where it is not drawn, so it does not
  // come back unasked on returning to one.
  if (drawerOpen && layout !== 'tablet') setDrawerOpen(false)
  const showingDrawer = drawerOpen && layout === 'tablet'
  // Which of the two views shows when no camera is open, kept in the address so
  // a view can be bookmarked or opened in a new tab (#282).
  const [view, setView] = useState<View>(() => viewOf(window.location.hash))
  useEffect(() => {
    const follow = () => setView(viewOf(window.location.hash))
    window.addEventListener('hashchange', follow)
    return () => window.removeEventListener('hashchange', follow)
  }, [])
  const shownView: View = view === 'alerts' && layout !== 'phone' ? 'sites' : view
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    fetchMe().then((me) => {
      setUser(me)
      applyTheme(me?.theme)
    })
  }, [])

  // Split from the subscription below on purpose: a manual refresh should
  // re-fetch without tearing down and reopening the event stream.
  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const data = await fetchClusters(100)
        if (cancelled) return
        setClusters(data)
        setClusterStatus('ok')
      } catch (e) {
        if (cancelled) return
        setClusterStatus('error')
        setClusterError(e instanceof Error ? e.message : String(e))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const es = new EventSource('/api/clusters/stream')
    // Every open after the first is a reconnect, and what was sent while the
    // stream was down is gone: arm state is read again rather than trusted.
    let opened = false
    es.onopen = () => {
      setClusterStatus('live')
      if (opened) refreshArming()
      opened = true
    }
    es.onmessage = (e) => {
      const c = JSON.parse(e.data) as Cluster & { is_new: boolean; removed?: boolean; new_note?: ClusterNote }
      if (c.removed) {
        setClusters((prev) => prev.filter(x => x.id !== c.id))
        setClusterNewIds((prev) => { const s = new Set(prev); s.delete(c.id); return s })
        return
      }
      setClusters((prev) => {
        // A refinement updates in place; only a genuinely new cluster reorders.
        const idx = prev.findIndex(x => x.id === c.id)
        if (idx === -1) return [c, ...prev].slice(0, 200)
        const next = prev.slice()
        next[idx] = c
        return next
      })
      if (c.is_new) {
        setAnnouncement(`New alert: ${incidentTitle(c)} at ${c.site_name || c.site_id}, ${c.camera}`)
        setClusterNewIds((prev) => new Set(prev).add(c.id))
        setTimeout(() => setClusterNewIds((prev) => { const s = new Set(prev); s.delete(c.id); return s }), 5500)
      }
      // selectionRef avoids reconnecting this EventSource on every click.
      if (c.new_note && c.id === selectionRef.current?.clusterId) {
        setNotesState((prev) => ({ id: prev.id, notes: [...prev.notes, c.new_note!] }))
      }
    }
    es.addEventListener('arm', (e) => onArm(JSON.parse((e as MessageEvent).data) as ArmEvent))
    es.addEventListener('arm_pending', (e) => onArmPending(JSON.parse((e as MessageEvent).data) as ArmPendingEvent))
    es.addEventListener('arm_postponed', (e) => onArmPostponed(JSON.parse((e as MessageEvent).data) as ArmPostponedEvent))
    es.onerror = () => setClusterStatus('error')
    return () => es.close()
  }, [onArm, onArmPending, onArmPostponed, refreshArming])

  useEffect(() => {
    selectionRef.current = selection
  }, [selection])

  // Reads only the id, so it refetches when the selected cluster changes but not
  // when an unrelated part of `selection` does.
  useEffect(() => {
    if (selectedClusterId === null) return
    let cancelled = false
    fetchClusterDetail(selectedClusterId)
      .then((d) => {
        if (cancelled) return
        setNotesState((prev) => {
          // Merge, don't overwrite — an SSE new_note may have arrived while
          // this fetch was in flight.
          const carried = prev.id === selectedClusterId ? prev.notes : []
          const byId = new Map(d.notes.map((n) => [n.id, n]))
          for (const n of carried) if (!byId.has(n.id)) byId.set(n.id, n)
          return {
            id: selectedClusterId,
            notes: Array.from(byId.values()).sort((a, b) => a.created_at - b.created_at || a.id - b.id),
          }
        })
      })
      .catch(() => { if (!cancelled) setNotesState({ id: selectedClusterId, notes: [] }) })
    return () => { cancelled = true }
  }, [selectedClusterId])

  const selectedCluster = selection ? clusters.find((c) => c.id === selection.clusterId) ?? null : null
  // The Action Center shows the site map while a camera is watched with no
  // incident open; an incident or the speak panel takes precedence.
  const showingMap = !speaking && panel?.type === 'stream' && !selectedCluster

  const identity = selection?.identity ?? null

  const shownSites = siteList.list.filter((s) => allowsSite(filter, s.id)).length
  const inProgress = visibleClusters.filter((c) => isInProgress(c)).length
  const wallSummary = [
    `${shownSites} ${shownSites === 1 ? 'site' : 'sites'}`,
    inProgress > 0 ? `${inProgress} in progress` : '',
  ].filter(Boolean).join(' · ')

  // A site chosen from the issues list that the filter hides, said over the
  // wall rather than scrolled to nothing; it holds until the filter changes.
  const [hiddenSite, setHiddenSite] = useState<string | null>(null)
  // Changing the filter also closes anything on screen the new filter would
  // hide — an open stream, or a selected incident. Done here, in the handler
  // that changes it, rather than in an effect watching for the change: the
  // dispatcher narrowed the view, so the view narrows as one action.
  const changeFilter = useCallback((next: SiteFilter) => {
    applyFilter(next)
    setHiddenSite(null)
    setPanel((p) => (p?.type === 'stream' && !allowsCamera(next, p.siteId, p.camera) ? null : p))
    setSelection((sel) => {
      if (sel === null) return null
      const c = clusters.find((x) => x.id === sel.clusterId)
      return c && allowsCamera(next, c.site_id, c.camera) ? sel : null
    })
  }, [applyFilter, clusters])


  // What a camera was opened from, so closing it puts focus back there rather
  // than on the page, a wall away from where a keyboard user was (#287). Kept
  // from the first camera opened; switching camera keeps it.
  const opener = useRef<{ el: Element | null; tile: string } | null>(null)
  const cameraEntry = useRef<string | null>(null)
  // The tile to focus once the wall has drawn it again.
  const [focusTile, setFocusTile] = useState<string | null>(null)
  const openStream = useCallback((siteId: string, camera: string) => {
    // A history entry for an open camera, so browser and Android Back close it
    // rather than leaving the app (#287). One per opening: switching camera
    // inside it keeps the entry, as it keeps what it was opened from.
    // Tagged with this opening: history outlives a reload, and an entry left by
    // a camera open before one read as "still open" to Back.
    if (!opener.current) {
      cameraEntry.current = `${Date.now()}-${Math.random()}`
      window.history.pushState({ ...window.history.state, camera: cameraEntry.current }, '')
    }
    opener.current ??= { el: document.activeElement, tile: `${siteId}/${camera}` }
    // Opening the camera already in view keeps its panel object, so the speak
    // panel — tied to it by identity — and any message it is playing stay put;
    // only a different camera closes it.
    setPanel((prev) =>
      prev?.type === 'stream' && prev.siteId === siteId && prev.camera === camera ? prev : { type: 'stream', siteId, camera })
  }, [])

  const handleSelect = useCallback((siteId: string, camera: string) => {
    openStream(siteId, camera)
    setSelection(null)
  }, [openStream])

  // A site chosen from the issues list is scrolled to on the wall, which is
  // shown first if a camera or incident covers it.
  const [scrollTo, setScrollTo] = useState<{ siteId: string; at: number }>()
  // Only the wall has a site to scroll to, so a choice made on Maps or the
  // phone's Alerts tab goes there first (#287).
  const handleSite = useCallback((siteId: string) => {
    setPanel(null)
    setSelection(null)
    if (window.location.hash !== '#/' && window.location.hash !== '') window.location.hash = '#/'
    setView('sites')
    setHiddenSite(allowsSite(filter, siteId) ? null : siteId)
    setScrollTo({ siteId, at: Date.now() })
  }, [filter, setView])
  useEffect(() => {
    if (!scrollTo) return
    document.querySelector(`section[data-site-id="${CSS.escape(scrollTo.siteId)}"]`)?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' })
  }, [scrollTo])

  // closePanel shuts the camera without touching history: Back has already
  // moved it, or a link to another view is about to.
  const closePanel = useCallback(() => {
    const back = opener.current
    opener.current = null
    cameraEntry.current = null
    // An alert card is still there to go back to; a tile is drawn afresh.
    if (back?.el instanceof HTMLElement && back.el.isConnected && back.el !== document.body) back.el.focus()
    else if (back) setFocusTile(back.tile)
    setPanel(null)
    setSelection(null)
  }, [setFocusTile])
  // handleClose is the camera's own back button: it goes back through the
  // entry its opening made, which closes it as browser Back does.
  const handleClose = useCallback(() => {
    if (cameraEntry.current && window.history.state?.camera === cameraEntry.current) window.history.back()
    else closePanel()
  }, [closePanel])
  useEffect(() => {
    const onPop = () => { if (window.history.state?.camera !== cameraEntry.current) closePanel() }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [closePanel])

  const handleClusterClick = useCallback((c: Cluster) => {
    openStream(c.site_id, c.camera)
    setSelection({ clusterId: c.id, identity: { id: c.primary_event_id ?? c.last_event_id, eventTime: c.end_time, label: c.label, score: c.score } })
  }, [openStream])

  // Opening a tamper incident from the indicator. Goes through the cluster
  // list rather than taking the id alone, so the stream panel opens on the
  // right camera exactly as clicking the card does.
  const handleOpenIncident = useCallback((clusterId: number) => {
    const c = clusters.find((x) => x.id === clusterId)
    if (c) handleClusterClick(c)
  }, [clusters, handleClusterClick])

  // Accepting a camera's view. The indicator stops reporting it on the next
  // poll rather than being corrected here: the server decides whether the
  // comparison now passes, and guessing locally would show it cleared even if
  // the camera is still covered.
  const handleAcceptView = useCallback(async (siteId: string, camera: string) => {
    try {
      await acceptCameraView(siteId, camera)
    } catch {
      // Left to the next poll: a failed accept means the state stands, which
      // is the safe direction.
    }
  }, [])

  // Only incidents already loaded; a tamper incident is pinned pending, so it
  // nearly always is, and a row without one simply shows no picture.
  const tamperPicture = useCallback((clusterId: number) => {
    const c = clusters.find((x) => x.id === clusterId)
    return c && imageUrl(c.primary_event_id ?? c.last_event_id, c.end_time)
  }, [clusters])

  const handleAcknowledge = useCallback(async (id: number, acknowledged: boolean) => {
    const updated = await updateClusterAck(id, acknowledged)
    setClusters((prev) => prev.map((c) => (c.id === id ? updated : c)))
  }, [])

  const handleAddNote = useCallback(async (id: number, text: string) => {
    // Not applied locally — the sender gets it back via the same SSE broadcast as everyone else.
    await addClusterNote(id, text)
  }, [])

  // latestOn is a camera's most recent incident among those shown.
  const latestOn = (siteId: string, camera: string) =>
    visibleClusters.filter((c) => c.site_id === siteId && c.camera === camera).reduce<Cluster | null>((best, c) => (!best || c.end_time > best.end_time ? c : best), null)

  // Where Back goes from a camera: the view it was opened from.
  const backLabel = shownView === 'alerts' ? 'Alerts' : shownView === 'maps' ? 'Maps' : 'Sites'

  // liveView is a camera's live stream, placed where CameraView says. Not
  // keyed by its place: moving between live view, an incident's corner and
  // full size keeps one stream, so a talk in progress carries on (#287).
  const liveView = (siteId: string, camera: string, layout: StreamLayout, overlay?: ReactNode, cornerTop?: number) => (
    <StreamPanel
      siteId={siteId}
      siteName={sites[siteId]?.name ?? siteId}
      camera={camera}
      offline={health[siteId]?.[camera] === 'offline'}
      onClose={handleClose}
      onSpeak={speakToPanel}
      heldBy={speaker.heldBy}
      onTalkEnded={speaker.refresh}
      speechAvailable={speaker.speechAvailable}
      layout={layout}
      overlay={overlay}
      cornerTop={cornerTop}
      backLabel={backLabel}
      picker={
        <CameraPicker
          sites={siteList.list.filter((s) => allowsSite(filter, s.id))}
          allows={(site, cam) => allowsCamera(filter, site, cam)}
          current={{ siteId, camera }}
          health={health}
          onPick={handleSelect}
        />
      }
    >
      {latestOn(siteId, camera) && (
        <LatestIncident cluster={latestOn(siteId, camera)!} onOpen={() => handleClusterClick(latestOn(siteId, camera)!)} />
      )}
    </StreamPanel>
  )

  const pending = visibleClusters.filter((c) => !c.acknowledged).length
  const disarmed = siteList.list.filter((s) => arming.state[s.id] && !arming.state[s.id].armed).map((s) => ({ id: s.id, name: s.name || s.id, arms: armsAt(arming.state[s.id]) ?? undefined }))
  const beside = layout === 'desktop'
  const side = speaking ? (
          <SpeakPanel
            siteName={sites[speaking.siteId]?.name ?? speaking.siteId}
            camera={speaking.camera}
            speak={(text, signal) => speak(speaking.siteId, speaking.camera, text, signal).finally(speaker.refresh)}
            onClose={() => setSpeakingFrom(null)}
            heldBy={speaker.heldBy}
            phrases={phrases}
          />
        ) : showingMap && panel?.type === 'stream' ? (
          <SiteMapPane
            siteId={panel.siteId}
            siteName={sites[panel.siteId]?.name ?? panel.siteId}
            watching={panel.camera}
            health={health[panel.siteId] ?? {}}
            tamper={tamper[panel.siteId] ?? {}}
            clusters={clusters}
            onSelectCamera={(camera) => handleSelect(panel.siteId, camera)}
          />
        ) : selectedCluster ? (
          <ClusterDispatchPanel
            cluster={selectedCluster}
            noRecordings={!!(selectedCluster && sites[selectedCluster.site_id]?.no_recordings)}
            identity={identity}
            notes={clusterNotes}
            onAcknowledge={handleAcknowledge}
            onAddNote={handleAddNote}
          />
        ) : null

  return (
    <div className="flex flex-col h-dvh bg-zinc-950 text-zinc-100">
      {/* First in the tab order: the header and every alert otherwise stood
          between a keyboard user and the wall. Focused rather than followed,
          since the address's hash is the view. */}
      <a
        href="#main-content"
        onClick={(e) => { e.preventDefault(); document.getElementById('main-content')?.focus() }}
        className="sr-only focus:not-sr-only focus:fixed focus:z-50 focus:top-2 focus:left-2 focus:px-3 focus:py-2 focus:rounded-md focus:bg-zinc-900 focus:text-zinc-100 focus:border focus:border-orange-400"
      >
        Skip to main content
      </a>
      <p role="status" className="sr-only">{announcement}</p>
      <header className="shrink-0 h-14 bg-zinc-900 border-b border-zinc-800 px-3 sm:px-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {layout === 'tablet' && (
            <button
              type="button"
              aria-expanded={showingDrawer}
              aria-label={['Alerts', pending > 0 && `${pending} pending`, disarmed.length > 0 && `${disarmed.length} ${disarmed.length === 1 ? 'site' : 'sites'} disarmed`].filter(Boolean).join(', ')}
              onClick={() => setDrawerOpen((o) => !o)}
              className={`h-9 px-3 flex items-center gap-2 rounded-md border text-sm font-semibold transition-colors ${showingDrawer ? 'border-orange-400 bg-orange-500/15 text-zinc-100' : 'border-zinc-800 bg-zinc-900 text-zinc-100 hover:bg-zinc-800'}`}
            >
              <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={phoneTabs[1].icon} /></svg>
              Alerts
              {pending > 0 && <span className="min-w-5 h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-orange-400 text-zinc-950 text-xs font-bold font-mono">{pending}</span>}
              {disarmed.length > 0 && <span aria-hidden="true" className="min-w-5 h-5 px-1.5 inline-flex items-center justify-center rounded-full border border-attention/55 bg-attention/10 text-attention text-xs font-bold font-mono">{disarmed.length}</span>}
            </button>
          )}
          <button onClick={handleClose} title="Home">
            <img src={owLogo} alt="Overwatch" width={129} height={28} className="h-7 w-auto shrink-0" />
          </button>
          {layout !== 'phone' && <nav aria-label="Views" className="flex gap-1">
            {views.map((v) => (
              <a
                key={v.key}
                href={v.href}
                aria-current={shownView === v.key ? 'page' : undefined}
                onClick={() => { setView(v.key); closePanel() }}
                className={`h-9 px-3 flex items-center rounded-md text-sm transition-colors ${
                  shownView === v.key ? 'bg-orange-500/15 text-orange-300 font-semibold' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'
                }`}
              >
                {v.name}
              </a>
            ))}
          </nav>}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <IssuesIndicator
            issues={issues}
            onSelectSite={handleSite}
            onOpenIncident={handleOpenIncident}
            onAcceptView={handleAcceptView}
            pictureOf={tamperPicture}
          />
          {/* The wall has its own; this keeps the filter in reach while a camera
              or incident covers it, and never shows the two at once. */}
          {panel && <FilterButton sites={siteList.list} filter={filter} onChange={changeFilter} compact />}
          {user?.console_url && (
            <a
              href={user.console_url}
              aria-label="Overwatch Console"
              className="h-9 px-2.5 flex items-center gap-1.5 rounded-md text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
            >
              <span className="hidden sm:inline">Console</span>
              <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 17 17 7M8 7h9v9" />
              </svg>
            </a>
          )}
          {user ? (
            <AccountMenu user={user} />
          ) : (
            <a href="/auth/logout" className="h-9 px-2.5 flex items-center rounded-md text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800">
              Sign out
            </a>
          )}
        </div>
      </header>

      <ArmAnnouncer notices={arming.notices} sites={siteList.byId} />
      <ArmNotices
        key={arming.notices.map(noticeKey).join(',')}
        notices={arming.notices}
        sites={siteList.byId}
        states={arming.state}
        busy={arming.busy}
        failures={arming.failures}
        reasons={arming.reasons}
        onArmNow={(siteId) => void arming.setArmed(siteId, true)}
        onPostpone={(siteId, minutes) => void arming.postpone(siteId, minutes)}
        onDismiss={arming.dismiss}
        onDismissFailure={arming.dismissFailure}
      />

      <div className={beside ? 'flex flex-1 min-h-0 relative' : 'flex-1 min-h-0 relative overflow-y-auto scrollbar-thin flex flex-col'}>
        {layout === 'desktop' && (
          <ClustersSidebar
            clusters={visibleClusters}
            narrowed={isNarrowed(filter)}
            newIds={clusterNewIds}
            selectedId={selection?.clusterId ?? null}
            status={clusterStatus}
            error={clusterError}
            disarmedSites={disarmed}
            onClusterClick={handleClusterClick}
            open={eventsSidebarOpen}
            onToggle={() => setEventsSidebarOpen((o) => !o)}
          />
        )}

        <main id="main-content" tabIndex={-1} className={beside ? 'flex-1 w-full min-w-0' : `w-full min-w-0 ${side ? 'shrink-0' : 'flex-1 min-h-0'}`}>
          {panel?.type === 'stream' ? (
            // Keyed by camera alone: a new camera starts a new stream, while an
            // incident opening or closing on this one keeps it.
            <CameraView
              key={`${panel.siteId}/${panel.camera}`}
              cluster={!speaking && selectedCluster && selectedCluster.site_id === panel.siteId && selectedCluster.camera === panel.camera ? selectedCluster : null}
              onBack={handleClose}
              backLabel={backLabel}
              corner={layout !== 'phone'}
              noRecordings={!!sites[panel.siteId]?.no_recordings}
              stream={(where, overlay, cornerTop) => liveView(panel.siteId, panel.camera, where, overlay, cornerTop)}
              onSpeak={speaker.speechAvailable !== false && canSpeak ? speakToPanel : undefined}
            />
          ) : shownView === 'alerts' ? (
            <ClustersSidebar
          clusters={visibleClusters}
          narrowed={isNarrowed(filter)}
          newIds={clusterNewIds}
          selectedId={selection?.clusterId ?? null}
          status={clusterStatus}
          error={clusterError}
          disarmedSites={disarmed} variant="page" open onToggle={() => {}} onClusterClick={handleClusterClick} />
          ) : shownView === 'maps' ? (
            <MapsPage
              sites={siteList.list.filter((s) => allowsSite(filter, s.id))}
              health={health}
              tamper={tamper}
              clusters={visibleClusters}
              onWatch={handleSelect}
              onOpenIncident={handleClusterClick}
            />
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex-1 relative overflow-y-auto scrollbar-thin px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-5">
                <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
                  <div className="flex flex-col gap-1 min-w-0">
                    <h1 className="text-2xl font-bold tracking-tight text-zinc-100">Sites</h1>
                    <p className="text-[13px] font-mono text-zinc-400 tabular-nums">{wallSummary}</p>
                  </div>
                  <div className="flex-1" />
                  <FilterButton sites={siteList.list} filter={filter} onChange={changeFilter} />
                  {/* One column on a phone, whatever the size. */}
                  {layout !== 'phone' && <TileSize value={cardSize} onChange={setCardSize} />}
                </div>
                {isNarrowed(filter) && (
                  <FilterStrip
                    sites={siteList.list}
                    filter={filter}
                    hidden={hiddenSite ? siteList.byId[hiddenSite]?.name || hiddenSite : undefined}
                    onClear={() => changeFilter(NO_FILTER)}
                  />
                )}
                <SiteDashboard
                  focusTile={focusTile}
                  onTileFocused={() => setFocusTile(null)}
                  sites={sites}
                  status={siteList.status}
                  clusters={visibleClusters}
                  filter={filter}
                  cardSize={cardSize}
                  health={health}
                  tamper={tamper}
                  onSelectCamera={handleSelect}
                  armed={arming.state}
                  onSetArmed={(siteId, armed) => void arming.setArmed(siteId, armed)}
                  armBusy={arming.busy}
                />
              </div>
            </div>
          )}
        </main>

        {/* Beside the view on a desktop; under it anywhere narrower, where a
            panel beside it left an open incident 56px (#284). */}
        {side && (beside ? side : (
          <div className="[&>aside]:w-full [&>aside]:border-l-0 [&>aside]:border-t [&>aside]:overflow-visible">{side}</div>
        ))}
      </div>

      {showingDrawer && (
        <Drawer label="Alerts" onClose={() => closeDrawer()}>
          <ClustersSidebar
            clusters={visibleClusters}
            narrowed={isNarrowed(filter)}
            newIds={clusterNewIds}
            selectedId={selection?.clusterId ?? null}
            status={clusterStatus}
            error={clusterError}
            disarmedSites={disarmed}
            variant="drawer"
            open
            onToggle={() => closeDrawer()}
            // The drawer hands focus back to the Alerts button as it closes;
            // the incident this opens then takes it on.
            onClusterClick={(c) => { closeDrawer(); handleClusterClick(c) }}
          />
        </Drawer>
      )}

      {layout === 'phone' && !panel && (
        <nav aria-label="Views" className="shrink-0 min-h-16 pb-[max(0.375rem,env(safe-area-inset-bottom))] grid grid-cols-3 bg-zinc-900 border-t border-zinc-800">
          {phoneTabs.map((v) => (
            <a
              key={v.key}
              href={v.href}
              aria-current={shownView === v.key ? 'page' : undefined}
              aria-label={v.key === 'alerts' && pending > 0 ? `Alerts, ${pending} pending` : undefined}
              onClick={() => setView(v.key)}
              className={`relative flex flex-col items-center justify-center gap-0.5 text-xs ${shownView === v.key ? 'text-orange-300 font-bold' : 'text-zinc-400 font-medium'}`}
            >
              <svg aria-hidden="true" className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={v.icon} /></svg>
              {v.name}
              {v.key === 'alerts' && pending > 0 && (
                <span aria-hidden="true" className="absolute top-1.5 left-[calc(50%+6px)] min-w-[18px] h-[18px] px-1 rounded-full bg-orange-400 text-zinc-950 text-[11px] font-bold leading-[18px] text-center">{pending}</span>
              )}
            </a>
          ))}
        </nav>
      )}
    </div>
  )
}
